import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Store, AlertCircle } from 'lucide-react';
import { ResponsiveImage } from '../ResponsiveImage';

const STORE_API = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';

// Orders in these statuses need merchant attention on the home dashboard.
const PENDING_STATUSES = new Set(['pending', 'preparing']);

const MAX_THUMBS = 4;

function StoreThumb({ store, pending }) {
    return (
        <div className="relative w-9 h-9 rounded-lg overflow-hidden shadow-sm ring-1 ring-white/70 bg-gradient-to-br from-secondary-500 to-secondary-600">
            {store.cover_image_url ? (
                <ResponsiveImage
                    src={store.cover_image_url}
                    alt={store.name}
                    fillContainer={true}
                    placeholderClassName="bg-gradient-to-br from-secondary-500 to-secondary-600"
                    containerClassName="absolute inset-0"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                    <Store size={14} strokeWidth={2.25} />
                </div>
            )}
            {pending > 0 && (
                <span className="absolute -top-1 -end-1 min-w-[14px] h-3.5 px-1 rounded-full bg-warning-500 text-white text-[9px] font-bold flex items-center justify-center shadow">
                    {pending > 9 ? '9+' : pending}
                </span>
            )}
        </div>
    );
}

/**
 * MerchantStoresTile — single WidgetCard-style tile that previews the
 * merchant's stores as mini thumbnails. Clicking opens the full stores
 * modal (`expandedWidget = 'stores'`) on the parent home page.
 */
export function MerchantStoresTile({ stores, chatId, safeFetch, onOpen }) {
    const [pendingByStore, setPendingByStore] = useState({});

    useEffect(() => {
        if (!chatId || chatId === 'guest' || !stores || stores.length === 0) return;
        let cancelled = false;

        const fetchPending = async () => {
            const entries = await Promise.all(
                stores.map(async (store) => {
                    try {
                        const url = `${STORE_API}/admin/store/${store.store_id}/orders?user_id=${encodeURIComponent(chatId)}`;
                        const res = await safeFetch(url, chatId);
                        if (!res.ok) return [store.store_id, 0];
                        const data = await res.json();
                        const count = (data.orders || []).filter(o => PENDING_STATUSES.has(o.status)).length;
                        return [store.store_id, count];
                    } catch (_) {
                        return [store.store_id, 0];
                    }
                })
            );
            if (!cancelled) setPendingByStore(Object.fromEntries(entries));
        };

        fetchPending();
        return () => { cancelled = true; };
    }, [stores, chatId, safeFetch]);

    if (!stores || stores.length === 0) return null;

    const totalPending = Object.values(pendingByStore).reduce((sum, n) => sum + n, 0);
    const shown = stores.slice(0, MAX_THUMBS);
    const overflow = stores.length - shown.length;
    const subtitle = totalPending > 0 ? `${totalPending} הזמנות לטיפול` : 'ניהול חנויות';

    return (
        <motion.button
            type="button"
            onClick={onOpen}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            className="group relative text-right overflow-hidden rounded-2xl bg-surface
                border border-line-subtle hover:border-line shadow-card hover:shadow-elevated
                transition-shadow duration-300 p-3.5 sm:p-4 flex flex-col
                focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400"
            aria-label="החנויות שלי"
        >
            <div aria-hidden="true" className="pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 bg-secondary-200/40" />

            <div className="relative flex items-center justify-between mb-2">
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl shadow-sm text-white bg-gradient-to-br from-secondary-500 to-secondary-600">
                    <Store size={18} strokeWidth={2.25} />
                </div>
                {totalPending > 0 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-2xs font-bold bg-warning-50 text-warning-700 border border-warning-100">
                        <AlertCircle size={10} strokeWidth={2.5} />
                        <span>{totalPending}</span>
                    </span>
                ) : (
                    <span className="text-2xl sm:text-3xl font-bold text-ink-strong leading-none tabular-nums">
                        {stores.length}
                    </span>
                )}
            </div>

            <div className="relative">
                <h3 className="text-sm sm:text-base font-bold text-ink-strong leading-tight">החנויות שלי</h3>
                <p className="text-2xs sm:text-xs text-ink-muted mt-0.5 leading-snug line-clamp-1">{subtitle}</p>
            </div>

            <div className="relative mt-2 flex items-center gap-1.5 flex-row-reverse justify-end">
                {shown.map(store => (
                    <StoreThumb
                        key={store.store_id}
                        store={store}
                        pending={pendingByStore[store.store_id] || 0}
                    />
                ))}
                {overflow > 0 && (
                    <div className="w-9 h-9 rounded-lg ring-1 ring-line bg-surface-sunken text-ink-muted text-xs font-bold flex items-center justify-center">
                        +{overflow}
                    </div>
                )}
            </div>
        </motion.button>
    );
}

export default MerchantStoresTile;
